import React, { useState, useEffect, useCallback } from 'react';

const API = 'https://api.botanix.in/api/horticulture';

const C = {
  accent:'#3AAF58', accentLight:'#EAF7EE', accentDark:'#2E8B47',
  bg:'#F3F6F3', surface:'#FFFFFF',
  border:'#E2E8E2', border2:'#CDD8CD',
  text:'#1A2E1E', text2:'#3D5E44', text3:'#7A9A80',
  amber:'#D4840F', amberBg:'#FEF6E7', amberBorder:'#F0D499',
  red:'#C83333', redBg:'#FDEAEA', redBorder:'#F0B8B8',
  blue:'#2563A8', blueBg:'#EAF1FB', blueBorder:'#B8D0F0',
  purple:'#6B48A8', purpleBg:'#F0EBFB', purpleBorder:'#C8B8F0',
  teal:'#1A7A7A', tealBg:'#E7F5F5', tealBorder:'#A8D8D8',
};

const ZONE_COLORS = {
  vegetable:'#3AAF58', flower:'#C83333', herb:'#D4840F',
  specialty:'#6B48A8', lawn:'#1A7A7A', nursery:'#2563A8',
  hydroponics:'#0A7A8A', microgreen:'#5A8A30', orchard:'#8A5A30',
  livestock:'#8A3A30', compost:'#6A5A30',
};

const inr = n => 'Rs.' + Number(n||0).toLocaleString('en-IN', { maximumFractionDigits:0 });
const today = () => new Date().toISOString().slice(0,10);

const Card = ({ children, style={}, className='' }) => (
  <div className={className} style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:12, boxShadow:'0 1px 4px rgba(0,0,0,.05)', ...style }}>{children}</div>
);

const Btn = ({ children, onClick, variant='primary', style={}, disabled }) => {
  const v = {
    primary:{ background:C.accent, color:'#fff', border:'none' },
    danger: { background:C.red, color:'#fff', border:'none' },
    ghost:  { background:'transparent', color:C.text2, border:`1px solid ${C.border2}` },
    light:  { background:C.accentLight, color:C.accent, border:'none' },
  };
  return <button onClick={onClick} disabled={disabled}
    style={{ ...v[variant], padding:'8px 16px', borderRadius:8, fontSize:13, fontWeight:600,
      cursor:disabled?'not-allowed':'pointer', opacity:disabled?0.5:1,
      display:'inline-flex', alignItems:'center', gap:6, whiteSpace:'nowrap', ...style }}>{children}</button>;
};

const Inp = ({ label, value, onChange, type='text', opts, placeholder='' }) => {
  const base = { width:'100%', padding:'9px 12px', background:C.bg, border:`1px solid ${C.border2}`, borderRadius:8, color:C.text, fontSize:13, boxSizing:'border-box' };
  return (
    <div style={{ marginBottom:12 }}>
      {label && <div style={{ fontSize:11, fontWeight:700, color:C.text3, textTransform:'uppercase', letterSpacing:'.05em', marginBottom:5 }}>{label}</div>}
      {opts
        ? <select style={base} value={value} onChange={e=>onChange(e.target.value)}>
            {opts.map(o => <option key={o.value||o} value={o.value||o}>{o.label||o}</option>)}
          </select>
        : <input type={type} style={base} value={value} onChange={e=>onChange(e.target.value)} placeholder={placeholder} />}
    </div>
  );
};

const Modal = ({ title, onClose, children, width='500px' }) => (
  <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.4)', zIndex:200, display:'flex', alignItems:'center', justifyContent:'center', padding:20 }}>
    <Card style={{ width, maxWidth:'100%', maxHeight:'90vh', overflow:'auto', padding:28 }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
        <div style={{ fontSize:17, fontWeight:800, color:C.text }}>{title}</div>
        <button onClick={onClose} style={{ background:C.bg, border:`1px solid ${C.border}`, borderRadius:8, width:32, height:32, cursor:'pointer', color:C.text2, fontSize:14 }}>X</button>
      </div>
      {children}
    </Card>
  </div>
);

const Badge = ({ label, color, bg, border }) => (
  <span style={{ display:'inline-flex', alignItems:'center', gap:4, padding:'2px 10px', borderRadius:99, fontSize:11, fontWeight:600, color, background:bg, border:`1px solid ${border}` }}>
    <span style={{ width:5, height:5, borderRadius:'50%', background:color, flexShrink:0 }} />{label}
  </span>
);

const Stat = ({ label, value, sub, color=C.accent, bg=C.accentLight, onClick }) => (
  <Card onClick={onClick} style={{ padding:18, cursor:onClick?'pointer':'default' }}>
    <div style={{ fontSize:11, fontWeight:700, color:C.text3, textTransform:'uppercase', letterSpacing:'.06em', marginBottom:8 }}>{label}</div>
    <div style={{ fontSize:26, fontWeight:800, color, letterSpacing:'-.02em' }}>{value}</div>
    {sub && <div style={{ fontSize:12, color:C.text3, marginTop:3 }}>{sub}</div>}
  </Card>
);

const Th = ({ c }) => <th style={{ padding:'10px 14px', fontSize:11, fontWeight:700, color:C.text3, textTransform:'uppercase', letterSpacing:'.06em', textAlign:'left', background:'#F5F8F5', borderBottom:`1px solid ${C.border}`, whiteSpace:'nowrap' }}>{c}</th>;
const Td = ({ children, style={} }) => <td style={{ padding:'11px 14px', fontSize:13, borderBottom:`1px solid ${C.border}`, verticalAlign:'middle', ...style }}>{children}</td>;
const Empty = ({ msg }) => <div style={{ padding:'40px 20px', textAlign:'center', color:C.text3, fontSize:14 }}>{msg}</div>;

const priorityColor = p => p==='high'?[C.red,C.redBg,C.redBorder]:p==='medium'?[C.amber,C.amberBg,C.amberBorder]:[C.accent,C.accentLight,'#A8D8B8'];

function HortDashboard({ zones, setTab }) {
  const [data, setData] = useState(null);
  const [tasks, setTasks] = useState([]);

  useEffect(() => {
    fetch(`${API}/dashboard`).then(r=>r.json()).then(setData).catch(()=>{});
    fetch(`${API}/tasks?status=pending`).then(r=>r.json()).then(d=>setTasks(Array.isArray(d)?d.slice(0,8):[])).catch(()=>{});
  }, []);

  const tasksByType = {};
  (data?.tasks||[]).forEach(t => { tasksByType[t.status] = parseInt(t.count); });

  return (
    <div style={{ padding:'24px 28px' }}>
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(150px,1fr))', gap:12, marginBottom:22 }}>
        <Stat label="Active Zones" value={zones.length} sub="All areas" color={C.accent} onClick={()=>setTab('zones')} />
        <Stat label="Tasks Pending" value={tasksByType['pending']||0} sub="Due today" color={C.amber} bg={C.amberBg} onClick={()=>setTab('tasks')} />
        <Stat label="Milk Today" value={`${data?.milk_today||0}L`} sub="From dairy" color={C.blue} bg={C.blueBg} onClick={()=>setTab('dairy')} />
        <Stat label="Pest Alerts" value={data?.pest_alerts||0} sub="Active" color={C.red} bg={C.redBg} onClick={()=>setTab('pests')} />
        <Stat label="Low Stock" value={data?.low_stock||0} sub="Supplies" color={C.purple} bg={C.purpleBg} onClick={()=>setTab('supplies')} />
        <Stat label="Compost" value={data?.compost_batches||0} sub="Processing" color={C.teal} bg={C.tealBg} onClick={()=>setTab('compost')} />
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'1.4fr 1fr', gap:16, marginBottom:16 }}>
        <Card style={{ padding:20 }}>
          <div style={{ fontSize:15, fontWeight:800, color:C.text, marginBottom:14 }}>Zone Overview</div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8 }}>
            {zones.map(z => (
              <div key={z.id} style={{ padding:'10px 12px', borderRadius:8, background:C.bg, border:`1px solid ${C.border}`,
                borderLeft:`3px solid ${ZONE_COLORS[z.category]||C.accent}` }}>
                <div style={{ fontSize:12, fontWeight:700, color:C.text }}>{z.name}</div>
                <div style={{ fontSize:11, color:C.text3, marginTop:2 }}>{(z.area_sqft||0).toLocaleString()} sqft</div>
                {z.plant_count > 0 && <div style={{ fontSize:11, color:C.accent, marginTop:2 }}>{z.plant_count} plants</div>}
              </div>
            ))}
          </div>
        </Card>

        <Card style={{ padding:20 }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:14 }}>
            <div style={{ fontSize:15, fontWeight:800, color:C.text }}>Pending Tasks</div>
            <Btn variant="light" onClick={()=>setTab('tasks')} style={{ padding:'4px 12px', fontSize:12 }}>View All</Btn>
          </div>
          {tasks.length === 0 ? <Empty msg="No pending tasks" /> : tasks.map(t => (
            <div key={t.id} style={{ padding:'10px 12px', borderRadius:8, background:C.bg, marginBottom:8,
              borderLeft:`3px solid ${priorityColor(t.priority)[0]}` }}>
              <div style={{ fontSize:13, fontWeight:600, color:C.text }}>{t.title}</div>
              <div style={{ fontSize:11, color:C.text3, marginTop:2 }}>{t.zone_name} - {t.scheduled_date?.slice(0,10)}</div>
            </div>
          ))}
        </Card>
      </div>

      <Card style={{ padding:20, background:'linear-gradient(135deg,#0A1A0F,#0F2A14)', border:'none' }}>
        <div style={{ fontSize:13, fontWeight:700, color:'#3AAF58', marginBottom:10, textTransform:'uppercase', letterSpacing:'.08em' }}>AI Insights</div>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(200px,1fr))', gap:12 }}>
          {[
            ['Spray Schedule', 'Next spray recommended in 3 days based on last treatment cycle'],
            ['Harvest Window', 'Vegetable garden harvest due in 7-10 days based on planting dates'],
            ['Compost Ready', 'Vermicompost batch estimated ready in 14 days'],
            ['Milk Trend', 'Morning yield consistently higher - optimize feeding schedule'],
          ].map(([title, insight]) => (
            <div key={title} style={{ padding:'12px 14px', background:'rgba(58,175,88,.08)', borderRadius:10, border:'1px solid rgba(58,175,88,.2)' }}>
              <div style={{ fontSize:12, fontWeight:700, color:'#3AAF58', marginBottom:5 }}>{title}</div>
              <div style={{ fontSize:12, color:'rgba(240,248,242,.7)', lineHeight:1.5 }}>{insight}</div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}

function ZonesTab({ zones, reload }) {
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ name:'', category:'vegetable', area_sqft:'', manager:'', location_notes:'' });
  const f = k => v => setForm(p=>({...p,[k]:v}));
  const cats = ['vegetable','flower','herb','specialty','lawn','nursery','hydroponics','microgreen','orchard','livestock','compost'];

  const save = async () => {
    await fetch(`${API}/zones`, { method:'POST', headers:{'Content-Type':'application/json'}, body:JSON.stringify(form) });
    setShowModal(false); reload();
  };

  return (
    <div style={{ padding:'24px 28px' }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
        <div style={{ fontSize:20, fontWeight:800, color:C.text }}>Garden Zones</div>
        <Btn onClick={()=>setShowModal(true)}>+ Add Zone</Btn>
      </div>
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(260px,1fr))', gap:14 }}>
        {zones.map(z => (
          <Card key={z.id} style={{ padding:20, borderTop:`4px solid ${ZONE_COLORS[z.category]||C.accent}` }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:10 }}>
              <div style={{ fontSize:15, fontWeight:800, color:C.text }}>{z.name}</div>
              <Badge label={z.category} color={ZONE_COLORS[z.category]||C.accent} bg={`${ZONE_COLORS[z.category]||C.accent}18`} border={`${ZONE_COLORS[z.category]||C.accent}33`} />
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8, marginTop:12 }}>
              {[['Area',`${(z.area_sqft||0).toLocaleString()} sqft`],['Plants',z.plant_count||0],['Manager',z.manager||'N/A'],['Status',z.status]].map(([l,v]) => (
                <div key={l} style={{ background:C.bg, borderRadius:7, padding:'8px 10px' }}>
                  <div style={{ fontSize:10, color:C.text3 }}>{l}</div>
                  <div style={{ fontSize:13, fontWeight:600, color:C.text, marginTop:2 }}>{v}</div>
                </div>
              ))}
            </div>
          </Card>
        ))}
      </div>
      {showModal && (
        <Modal title="Add Zone" onClose={()=>setShowModal(false)}>
          <Inp label="Zone Name" value={form.name} onChange={f('name')} placeholder="e.g. Vegetable Garden Block B" />
          <Inp label="Category" value={form.category} onChange={f('category')} opts={cats.map(c=>({value:c,label:c.charAt(0).toUpperCase()+c.slice(1)}))} />
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
            <Inp label="Area (sqft)" value={form.area_sqft} onChange={f('area_sqft')} type="number" />
            <Inp label="Manager" value={form.manager} onChange={f('manager')} placeholder="Staff name" />
          </div>
          <Inp label="Location Notes" value={form.location_notes} onChange={f('location_notes')} placeholder="Near main entrance..." />
          <div style={{ display:'flex', gap:10 }}>
            <Btn onClick={save} style={{ flex:1, justifyContent:'center' }}>Save Zone</Btn>
            <Btn variant="ghost" onClick={()=>setShowModal(false)} style={{ flex:1, justifyContent:'center' }}>Cancel</Btn>
          </div>
        </Modal>
      )}
    </div>
  );
}

function PlantsTab({ zones }) {
  const [plants, setPlants] = useState([]);
  const [filterZone, setFilterZone] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ zone_id:'', name:'', botanical_name:'', category:'vegetable', quantity:'', unit:'plants', planted_date:today(), expected_harvest:'', notes:'' });
  const f = k => v => setForm(p=>({...p,[k]:v}));

  const load = useCallback(() => {
    const q = filterZone ? `?zone_id=${filterZone}` : '';
    fetch(`${API}/plants${q}`).then(r=>r.json()).then(d=>setPlants(Array.isArray(d)?d:[])).catch(()=>{});
  }, [filterZone]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    await fetch(`${API}/plants`, { method:'POST', headers:{'Content-Type':'application/json'}, body:JSON.stringify(form) });
    setShowModal(false); load();
  };

  const statusColor = s => s==='growing'?[C.accent,C.accentLight,'#A8D8B8']:s==='harvested'?[C.blue,C.blueBg,C.blueBorder]:s==='dormant'?[C.text3,C.border,C.border2]:[C.red,C.redBg,C.redBorder];

  return (
    <div style={{ padding:'24px 28px' }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
        <div style={{ fontSize:20, fontWeight:800, color:C.text }}>Plant Inventory</div>
        <div style={{ display:'flex', gap:10 }}>
          <select value={filterZone} onChange={e=>setFilterZone(e.target.value)}
            style={{ padding:'7px 12px', background:C.surface, border:`1px solid ${C.border2}`, borderRadius:8, color:C.text, fontSize:13 }}>
            <option value="">All Zones</option>
            {zones.map(z=><option key={z.id} value={z.id}>{z.name}</option>)}
          </select>
          <Btn onClick={()=>setShowModal(true)}>+ Add Plant</Btn>
        </div>
      </div>
      <Card>
        <div style={{ overflowX:'auto' }}>
          <table style={{ width:'100%', borderCollapse:'collapse' }}>
            <thead><tr><Th c="Plant" /><Th c="Zone" /><Th c="Qty" /><Th c="Planted" /><Th c="Harvest Due" /><Th c="Status" /></tr></thead>
            <tbody>
              {plants.length===0 ? <tr><td colSpan={6}><Empty msg="No plants recorded yet" /></td></tr>
                : plants.map(p => {
                  const [col,bg,brd] = statusColor(p.status);
                  return (
                    <tr key={p.id}>
                      <Td><div style={{ fontWeight:600 }}>{p.name}</div>{p.botanical_name&&<div style={{ fontSize:11, color:C.text3, fontStyle:'italic' }}>{p.botanical_name}</div>}</Td>
                      <Td style={{ color:C.text3 }}>{p.zone_name}</Td>
                      <Td><span style={{ fontWeight:700, color:C.accent }}>{p.quantity}</span> {p.unit}</Td>
                      <Td style={{ fontSize:12, color:C.text3 }}>{p.planted_date?.slice(0,10)||'-'}</Td>
                      <Td style={{ fontSize:12, color:C.text3 }}>{p.expected_harvest?.slice(0,10)||'-'}</Td>
                      <Td><Badge label={p.status} color={col} bg={bg} border={brd} /></Td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
      </Card>
      {showModal && (
        <Modal title="Add Plant" onClose={()=>setShowModal(false)}>
          <Inp label="Zone" value={form.zone_id} onChange={f('zone_id')} opts={[{value:'',label:'Select zone'},...zones.map(z=>({value:z.id,label:z.name}))]} />
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
            <Inp label="Plant Name" value={form.name} onChange={f('name')} placeholder="e.g. Tomato" />
            <Inp label="Botanical Name" value={form.botanical_name} onChange={f('botanical_name')} placeholder="Optional" />
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
            <Inp label="Quantity" value={form.quantity} onChange={f('quantity')} type="number" />
            <Inp label="Unit" value={form.unit} onChange={f('unit')} opts={['plants','seeds','cuttings','bulbs','kg','trays']} />
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
            <Inp label="Planted Date" value={form.planted_date} onChange={f('planted_date')} type="date" />
            <Inp label="Expected Harvest" value={form.expected_harvest} onChange={f('expected_harvest')} type="date" />
          </div>
          <Inp label="Notes" value={form.notes} onChange={f('notes')} placeholder="Variety, source, care notes..." />
          <div style={{ display:'flex', gap:10 }}>
            <Btn onClick={save} style={{ flex:1, justifyContent:'center' }}>Save Plant</Btn>
            <Btn variant="ghost" onClick={()=>setShowModal(false)} style={{ flex:1, justifyContent:'center' }}>Cancel</Btn>
          </div>
        </Modal>
      )}
    </div>
  );
}

function TasksTab({ zones }) {
  const [tasks, setTasks] = useState([]);
  const [filterStatus, setFilterStatus] = useState('pending');
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ zone_id:'', task_type:'watering', title:'', description:'', scheduled_date:today(), assigned_to:'', priority:'medium' });
  const f = k => v => setForm(p=>({...p,[k]:v}));
  const taskTypes = ['watering','fertilizing','spraying','pruning','harvesting','planting','weeding','inspection','mowing','composting','other'];

  const load = useCallback(() => {
    const q = filterStatus === 'all' ? '' : `?status=${filterStatus}`;
    fetch(`${API}/tasks${q}`).then(r=>r.json()).then(d=>setTasks(Array.isArray(d)?d:[])).catch(()=>{});
  }, [filterStatus]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    await fetch(`${API}/tasks`, { method:'POST', headers:{'Content-Type':'application/json'}, body:JSON.stringify(form) });
    setShowModal(false); load();
  };

  const complete = async (id) => {
    await fetch(`${API}/tasks/${id}/complete`, { method:'PATCH', headers:{'Content-Type':'application/json'}, body:JSON.stringify({ notes:'Completed' }) });
    load();
  };

  return (
    <div style={{ padding:'24px 28px' }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
        <div style={{ fontSize:20, fontWeight:800, color:C.text }}>Tasks & Schedule</div>
        <div style={{ display:'flex', gap:10 }}>
          {['pending','completed','all'].map(s => (
            <button key={s} onClick={()=>setFilterStatus(s)}
              style={{ padding:'6px 14px', borderRadius:8, border:`1px solid ${filterStatus===s?C.accent:C.border2}`,
                background:filterStatus===s?C.accentLight:'transparent', color:filterStatus===s?C.accent:C.text3, fontSize:12, fontWeight:600, cursor:'pointer' }}>
              {s.charAt(0).toUpperCase()+s.slice(1)}
            </button>
          ))}
          <Btn onClick={()=>setShowModal(true)}>+ Add Task</Btn>
        </div>
      </div>
      <Card>
        <div style={{ overflowX:'auto' }}>
          <table style={{ width:'100%', borderCollapse:'collapse' }}>
            <thead><tr><Th c="Task" /><Th c="Zone" /><Th c="Type" /><Th c="Date" /><Th c="Assigned" /><Th c="Priority" /><Th c="Action" /></tr></thead>
            <tbody>
              {tasks.length===0 ? <tr><td colSpan={7}><Empty msg="No tasks found" /></td></tr>
                : tasks.map(t => {
                  const [col,bg,brd] = priorityColor(t.priority);
                  const isOverdue = t.scheduled_date < today() && t.status === 'pending';
                  return (
                    <tr key={t.id}>
                      <Td><div style={{ fontWeight:600 }}>{t.title}</div></Td>
                      <Td style={{ color:C.text3 }}>{t.zone_name||'-'}</Td>
                      <Td><span style={{ fontSize:12, background:C.bg, padding:'2px 8px', borderRadius:6, color:C.text2 }}>{t.task_type}</span></Td>
                      <Td style={{ fontSize:12, color:isOverdue?C.red:C.text3, fontWeight:isOverdue?700:400 }}>{t.scheduled_date?.slice(0,10)}{isOverdue&&' OVERDUE'}</Td>
                      <Td style={{ color:C.text3 }}>{t.assigned_to||'-'}</Td>
                      <Td><Badge label={t.priority} color={col} bg={bg} border={brd} /></Td>
                      <Td>{t.status==='pending' && <Btn onClick={()=>complete(t.id)} variant="light" style={{ padding:'4px 12px', fontSize:12 }}>Done</Btn>}</Td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
      </Card>
      {showModal && (
        <Modal title="Add Task" onClose={()=>setShowModal(false)}>
          <Inp label="Task Title" value={form.title} onChange={f('title')} placeholder="e.g. Spray rose garden for aphids" />
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
            <Inp label="Zone" value={form.zone_id} onChange={f('zone_id')} opts={[{value:'',label:'All Zones'},...zones.map(z=>({value:z.id,label:z.name}))]} />
            <Inp label="Task Type" value={form.task_type} onChange={f('task_type')} opts={taskTypes} />
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
            <Inp label="Scheduled Date" value={form.scheduled_date} onChange={f('scheduled_date')} type="date" />
            <Inp label="Priority" value={form.priority} onChange={f('priority')} opts={['low','medium','high']} />
          </div>
          <Inp label="Assigned To" value={form.assigned_to} onChange={f('assigned_to')} placeholder="Staff name" />
          <Inp label="Description" value={form.description} onChange={f('description')} placeholder="Details..." />
          <div style={{ display:'flex', gap:10 }}>
            <Btn onClick={save} style={{ flex:1, justifyContent:'center' }}>Save Task</Btn>
            <Btn variant="ghost" onClick={()=>setShowModal(false)} style={{ flex:1, justifyContent:'center' }}>Cancel</Btn>
          </div>
        </Modal>
      )}
    </div>
  );
}

function HarvestsTab({ zones }) {
  const [harvests, setHarvests] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ zone_id:'', harvest_date:today(), quantity:'', unit:'kg', quality:'good', destination:'kitchen', revenue:'0', notes:'' });
  const f = k => v => setForm(p=>({...p,[k]:v}));

  const load = () => { fetch(`${API}/harvests`).then(r=>r.json()).then(d=>setHarvests(Array.isArray(d)?d:[])).catch(()=>{}); };
  useEffect(() => { load(); }, []);

  const save = async () => {
    await fetch(`${API}/harvests`, { method:'POST', headers:{'Content-Type':'application/json'}, body:JSON.stringify(form) });
    setShowModal(false); load();
  };

  const totalRev = harvests.reduce((s,h)=>s+parseFloat(h.revenue||0),0);
  const totalQty = harvests.reduce((s,h)=>s+parseFloat(h.quantity||0),0);

  return (
    <div style={{ padding:'24px 28px' }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
        <div style={{ fontSize:20, fontWeight:800, color:C.text }}>Harvest Log</div>
        <Btn onClick={()=>setShowModal(true)}>+ Log Harvest</Btn>
      </div>
      <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:12, marginBottom:18 }}>
        <Stat label="Total Records" value={harvests.length} sub="All harvests" />
        <Stat label="Total Quantity" value={`${totalQty.toFixed(1)} kg`} sub="All produce" color={C.blue} bg={C.blueBg} />
        <Stat label="Total Revenue" value={inr(totalRev)} sub="From sales" color={C.purple} bg={C.purpleBg} />
      </div>
      <Card>
        <div style={{ overflowX:'auto' }}>
          <table style={{ width:'100%', borderCollapse:'collapse' }}>
            <thead><tr><Th c="Date" /><Th c="Zone" /><Th c="Plant" /><Th c="Quantity" /><Th c="Quality" /><Th c="Destination" /><Th c="Revenue" /></tr></thead>
            <tbody>
              {harvests.length===0 ? <tr><td colSpan={7}><Empty msg="No harvests recorded yet" /></td></tr>
                : harvests.map(h => (
                  <tr key={h.id}>
                    <Td style={{ fontSize:12, color:C.text3 }}>{h.harvest_date?.slice(0,10)}</Td>
                    <Td style={{ color:C.text3 }}>{h.zone_name||'-'}</Td>
                    <Td><span style={{ fontWeight:600 }}>{h.plant_name||'General'}</span></Td>
                    <Td><span style={{ fontWeight:700, color:C.accent }}>{h.quantity}</span> {h.unit}</Td>
                    <Td style={{ color:C.text3 }}>{h.quality}</Td>
                    <Td style={{ color:C.text3 }}>{h.destination}</Td>
                    <Td><span style={{ fontWeight:600, color:parseFloat(h.revenue)>0?C.accent:C.text3 }}>{inr(h.revenue)}</span></Td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </Card>
      {showModal && (
        <Modal title="Log Harvest" onClose={()=>setShowModal(false)}>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
            <Inp label="Zone" value={form.zone_id} onChange={f('zone_id')} opts={[{value:'',label:'Select zone'},...zones.map(z=>({value:z.id,label:z.name}))]} />
            <Inp label="Harvest Date" value={form.harvest_date} onChange={f('harvest_date')} type="date" />
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
            <Inp label="Quantity" value={form.quantity} onChange={f('quantity')} type="number" />
            <Inp label="Unit" value={form.unit} onChange={f('unit')} opts={['kg','grams','pieces','bunches','litres','trays']} />
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
            <Inp label="Quality" value={form.quality} onChange={f('quality')} opts={['excellent','good','average','poor']} />
            <Inp label="Destination" value={form.destination} onChange={f('destination')} opts={['kitchen','sold','compost','nursery','personal']} />
          </div>
          <Inp label="Revenue if sold (Rs)" value={form.revenue} onChange={f('revenue')} type="number" placeholder="0" />
          <Inp label="Notes" value={form.notes} onChange={f('notes')} placeholder="Variety, special notes..." />
          <div style={{ display:'flex', gap:10 }}>
            <Btn onClick={save} style={{ flex:1, justifyContent:'center' }}>Save Harvest</Btn>
            <Btn variant="ghost" onClick={()=>setShowModal(false)} style={{ flex:1, justifyContent:'center' }}>Cancel</Btn>
          </div>
        </Modal>
      )}
    </div>
  );
}

function DairyTab() {
  const [livestock, setLivestock] = useState([]);
  const [milkLog, setMilkLog] = useState([]);
  const [showAnimal, setShowAnimal] = useState(false);
  const [showMilk, setShowMilk] = useState(false);
  const [animalForm, setAnimalForm] = useState({ tag_number:'', name:'', breed:'', date_of_birth:'' });
  const [milkForm, setMilkForm] = useState({ animal_id:'', log_date:today(), session:'morning', quantity_litres:'', quality:'good', destination:'kitchen', notes:'' });
  const af = k => v => setAnimalForm(p=>({...p,[k]:v}));
  const mf = k => v => setMilkForm(p=>({...p,[k]:v}));

  const load = () => {
    fetch(`${API}/livestock`).then(r=>r.json()).then(d=>setLivestock(Array.isArray(d)?d:[])).catch(()=>{});
    fetch(`${API}/milk`).then(r=>r.json()).then(d=>setMilkLog(Array.isArray(d)?d:[])).catch(()=>{});
  };
  useEffect(() => { load(); }, []);

  const saveAnimal = async () => {
    await fetch(`${API}/livestock`, { method:'POST', headers:{'Content-Type':'application/json'}, body:JSON.stringify(animalForm) });
    setShowAnimal(false); load();
  };
  const saveMilk = async () => {
    await fetch(`${API}/milk`, { method:'POST', headers:{'Content-Type':'application/json'}, body:JSON.stringify(milkForm) });
    setShowMilk(false); load();
  };

  const todayMilk = milkLog.filter(m=>m.log_date?.slice(0,10)===today()).reduce((s,m)=>s+parseFloat(m.quantity_litres||0),0);
  const monthMilk = milkLog.reduce((s,m)=>s+parseFloat(m.quantity_litres||0),0);

  return (
    <div style={{ padding:'24px 28px' }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
        <div style={{ fontSize:20, fontWeight:800, color:C.text }}>Dairy Unit</div>
        <div style={{ display:'flex', gap:10 }}>
          <Btn variant="ghost" onClick={()=>setShowAnimal(true)}>+ Add Animal</Btn>
          <Btn onClick={()=>setShowMilk(true)}>+ Log Milk</Btn>
        </div>
      </div>
      <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:12, marginBottom:18 }}>
        <Stat label="Total Animals" value={livestock.length} sub="Active" />
        <Stat label="Milk Today" value={`${todayMilk.toFixed(1)} L`} sub="All sessions" color={C.blue} bg={C.blueBg} />
        <Stat label="This Month" value={`${monthMilk.toFixed(1)} L`} sub="Total yield" color={C.purple} bg={C.purpleBg} />
      </div>
      <Card style={{ padding:20, marginBottom:16 }}>
        <div style={{ fontSize:15, fontWeight:800, color:C.text, marginBottom:14 }}>Animals</div>
        {livestock.length===0 ? <Empty msg="No animals registered" /> : (
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(180px,1fr))', gap:10 }}>
            {livestock.map(a => (
              <div key={a.id} style={{ padding:'14px 16px', background:C.bg, borderRadius:10, border:`1px solid ${C.border}` }}>
                <div style={{ fontSize:14, fontWeight:700, color:C.text }}>{a.name||'Unnamed'}</div>
                <div style={{ fontSize:12, color:C.text3 }}>Tag: {a.tag_number}</div>
                <div style={{ fontSize:12, color:C.text3 }}>{a.breed||'N/A'}</div>
                <div style={{ fontSize:15, fontWeight:700, color:C.accent, marginTop:8 }}>{parseFloat(a.total_milk_today||0).toFixed(1)} L today</div>
              </div>
            ))}
          </div>
        )}
      </Card>
      <Card>
        <div style={{ padding:'14px 18px', borderBottom:`1px solid ${C.border}`, fontSize:15, fontWeight:800, color:C.text }}>Milk Log</div>
        <div style={{ overflowX:'auto' }}>
          <table style={{ width:'100%', borderCollapse:'collapse' }}>
            <thead><tr><Th c="Date" /><Th c="Animal" /><Th c="Session" /><Th c="Quantity" /><Th c="Quality" /><Th c="Destination" /></tr></thead>
            <tbody>
              {milkLog.length===0 ? <tr><td colSpan={6}><Empty msg="No milk logs yet" /></td></tr>
                : milkLog.map(m => (
                  <tr key={m.id}>
                    <Td style={{ fontSize:12, color:C.text3 }}>{m.log_date?.slice(0,10)}</Td>
                    <Td>{m.animal_name||'All'}</Td>
                    <Td><span style={{ fontSize:12, background:C.bg, padding:'2px 8px', borderRadius:6 }}>{m.session}</span></Td>
                    <Td><span style={{ fontWeight:700, color:C.accent }}>{m.quantity_litres} L</span></Td>
                    <Td style={{ color:C.text3 }}>{m.quality}</Td>
                    <Td style={{ color:C.text3 }}>{m.destination}</Td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </Card>
      {showAnimal && (
        <Modal title="Add Animal" onClose={()=>setShowAnimal(false)}>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
            <Inp label="Tag Number" value={animalForm.tag_number} onChange={af('tag_number')} placeholder="COW-001" />
            <Inp label="Name" value={animalForm.name} onChange={af('name')} placeholder="e.g. Lakshmi" />
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
            <Inp label="Breed" value={animalForm.breed} onChange={af('breed')} placeholder="e.g. Gir, HF, Jersey" />
            <Inp label="Date of Birth" value={animalForm.date_of_birth} onChange={af('date_of_birth')} type="date" />
          </div>
          <div style={{ display:'flex', gap:10 }}>
            <Btn onClick={saveAnimal} style={{ flex:1, justifyContent:'center' }}>Save</Btn>
            <Btn variant="ghost" onClick={()=>setShowAnimal(false)} style={{ flex:1, justifyContent:'center' }}>Cancel</Btn>
          </div>
        </Modal>
      )}
      {showMilk && (
        <Modal title="Log Milk" onClose={()=>setShowMilk(false)}>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
            <Inp label="Animal" value={milkForm.animal_id} onChange={mf('animal_id')} opts={[{value:'',label:'All Combined'},...livestock.map(a=>({value:a.id,label:`${a.name||'Unnamed'} (${a.tag_number})`}))]} />
            <Inp label="Session" value={milkForm.session} onChange={mf('session')} opts={['morning','evening','night']} />
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
            <Inp label="Date" value={milkForm.log_date} onChange={mf('log_date')} type="date" />
            <Inp label="Quantity (Litres)" value={milkForm.quantity_litres} onChange={mf('quantity_litres')} type="number" placeholder="8.5" />
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
            <Inp label="Quality" value={milkForm.quality} onChange={mf('quality')} opts={['excellent','good','average','poor']} />
            <Inp label="Destination" value={milkForm.destination} onChange={mf('destination')} opts={['kitchen','sold','calf','wasted']} />
          </div>
          <div style={{ display:'flex', gap:10 }}>
            <Btn onClick={saveMilk} style={{ flex:1, justifyContent:'center' }}>Save</Btn>
            <Btn variant="ghost" onClick={()=>setShowMilk(false)} style={{ flex:1, justifyContent:'center' }}>Cancel</Btn>
          </div>
        </Modal>
      )}
    </div>
  );
}

function CompostTab() {
  const [batches, setBatches] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ type:'vermicompost', input_material:'', quantity_kg:'', ready_date:'', notes:'' });
  const f = k => v => setForm(p=>({...p,[k]:v}));

  const load = () => { fetch(`${API}/compost`).then(r=>r.json()).then(d=>setBatches(Array.isArray(d)?d:[])).catch(()=>{}); };
  useEffect(() => { load(); }, []);

  const save = async () => {
    await fetch(`${API}/compost`, { method:'POST', headers:{'Content-Type':'application/json'}, body:JSON.stringify(form) });
    setShowModal(false); load();
  };

  const markReady = async (id) => {
    const kg = window.prompt('Output quantity (kg)?');
    if (!kg) return;
    await fetch(`${API}/compost/${id}`, { method:'PATCH', headers:{'Content-Type':'application/json'}, body:JSON.stringify({ status:'ready', output_kg:kg }) });
    load();
  };

  const statusColor = s => s==='ready'?[C.accent,C.accentLight,'#A8D8B8']:s==='processing'?[C.amber,C.amberBg,C.amberBorder]:[C.blue,C.blueBg,C.blueBorder];

  return (
    <div style={{ padding:'24px 28px' }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
        <div style={{ fontSize:20, fontWeight:800, color:C.text }}>Compost & Vermicompost</div>
        <Btn onClick={()=>setShowModal(true)}>+ New Batch</Btn>
      </div>
      <Card style={{ padding:18, marginBottom:16, background:'linear-gradient(135deg,#1A2E1E,#0A1A0F)', border:'none' }}>
        <div style={{ fontSize:13, fontWeight:700, color:C.accent, marginBottom:8 }}>Mycorrhizae Development Notes</div>
        <div style={{ fontSize:12, color:'rgba(240,248,242,.7)', lineHeight:1.6 }}>
          For in-house mycorrhizae production: Maintain moisture 60-70%, temperature 20-25C. Use sterilized substrate (vermiculite + peat). Inoculate with host plant roots. Harvest spores after 8-12 weeks.
        </div>
      </Card>
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(300px,1fr))', gap:14 }}>
        {batches.length===0 ? <Card style={{ padding:40, textAlign:'center', color:C.text3 }}>No compost batches yet</Card>
          : batches.map(b => {
            const [col,bg,brd] = statusColor(b.status);
            return (
              <Card key={b.id} style={{ padding:20 }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:10 }}>
                  <div style={{ fontSize:14, fontWeight:700, color:C.text }}>{b.type.charAt(0).toUpperCase()+b.type.slice(1)}</div>
                  <Badge label={b.status} color={col} bg={bg} border={brd} />
                </div>
                <div style={{ fontSize:12, color:C.text2, marginBottom:10 }}>{b.input_material}</div>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8, marginBottom:12 }}>
                  <div style={{ background:C.bg, borderRadius:7, padding:'8px 10px' }}>
                    <div style={{ fontSize:10, color:C.text3 }}>Input</div>
                    <div style={{ fontSize:13, fontWeight:600 }}>{b.quantity_kg} kg</div>
                  </div>
                  <div style={{ background:C.bg, borderRadius:7, padding:'8px 10px' }}>
                    <div style={{ fontSize:10, color:C.text3 }}>Ready By</div>
                    <div style={{ fontSize:13, fontWeight:600 }}>{b.ready_date?.slice(0,10)||'TBD'}</div>
                  </div>
                </div>
                {b.status==='processing' && (
                  <Btn variant="light" onClick={()=>markReady(b.id)} style={{ width:'100%', justifyContent:'center', fontSize:12 }}>Mark as Ready</Btn>
                )}
                {b.output_kg && <div style={{ fontSize:12, color:C.accent, fontWeight:700, marginTop:8 }}>Output: {b.output_kg} kg</div>}
              </Card>
            );
          })}
      </div>
      {showModal && (
        <Modal title="New Compost Batch" onClose={()=>setShowModal(false)}>
          <Inp label="Type" value={form.type} onChange={f('type')} opts={['vermicompost','regular compost','mycorrhizae culture','bokashi','leaf mould']} />
          <Inp label="Input Material" value={form.input_material} onChange={f('input_material')} placeholder="e.g. Kitchen waste + garden clippings + cow dung" />
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
            <Inp label="Quantity (kg)" value={form.quantity_kg} onChange={f('quantity_kg')} type="number" />
            <Inp label="Expected Ready Date" value={form.ready_date} onChange={f('ready_date')} type="date" />
          </div>
          <Inp label="Notes" value={form.notes} onChange={f('notes')} placeholder="Special conditions, inoculants..." />
          <div style={{ display:'flex', gap:10 }}>
            <Btn onClick={save} style={{ flex:1, justifyContent:'center' }}>Start Batch</Btn>
            <Btn variant="ghost" onClick={()=>setShowModal(false)} style={{ flex:1, justifyContent:'center' }}>Cancel</Btn>
          </div>
        </Modal>
      )}
    </div>
  );
}

function SuppliesTab() {
  const [supplies, setSupplies] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ name:'', category:'pesticide', quantity:'', unit:'litres', reorder_level:'', cost_per_unit:'', supplier:'' });
  const f = k => v => setForm(p=>({...p,[k]:v}));

  const load = () => { fetch(`${API}/supplies`).then(r=>r.json()).then(d=>setSupplies(Array.isArray(d)?d:[])).catch(()=>{}); };
  useEffect(() => { load(); }, []);

  const save = async () => {
    await fetch(`${API}/supplies`, { method:'POST', headers:{'Content-Type':'application/json'}, body:JSON.stringify(form) });
    setShowModal(false); load();
  };

  const lowStock = supplies.filter(s=>parseFloat(s.quantity)<=parseFloat(s.reorder_level));

  return (
    <div style={{ padding:'24px 28px' }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
        <div style={{ fontSize:20, fontWeight:800, color:C.text }}>Supplies & Inventory</div>
        <Btn onClick={()=>setShowModal(true)}>+ Add Supply</Btn>
      </div>
      {lowStock.length > 0 && (
        <div style={{ background:C.redBg, border:`1px solid ${C.redBorder}`, borderRadius:10, padding:'12px 16px', marginBottom:16, fontSize:13, color:C.red, fontWeight:500 }}>
          {lowStock.length} item(s) below reorder level: {lowStock.map(s=>s.name).join(', ')}
        </div>
      )}
      <Card>
        <div style={{ overflowX:'auto' }}>
          <table style={{ width:'100%', borderCollapse:'collapse' }}>
            <thead><tr><Th c="Item" /><Th c="Category" /><Th c="Stock" /><Th c="Reorder At" /><Th c="Cost/Unit" /><Th c="Supplier" /><Th c="Status" /></tr></thead>
            <tbody>
              {supplies.length===0 ? <tr><td colSpan={7}><Empty msg="No supplies added yet" /></td></tr>
                : supplies.map(s => {
                  const isLow = parseFloat(s.quantity)<=parseFloat(s.reorder_level);
                  return (
                    <tr key={s.id}>
                      <Td><span style={{ fontWeight:600 }}>{s.name}</span></Td>
                      <Td><span style={{ fontSize:12, background:C.bg, padding:'2px 8px', borderRadius:6 }}>{s.category}</span></Td>
                      <Td><span style={{ fontWeight:700, color:isLow?C.red:C.accent }}>{s.quantity} {s.unit}</span></Td>
                      <Td style={{ color:C.text3 }}>{s.reorder_level} {s.unit}</Td>
                      <Td style={{ color:C.text3 }}>{inr(s.cost_per_unit)}</Td>
                      <Td style={{ color:C.text3 }}>{s.supplier||'-'}</Td>
                      <Td>{isLow ? <Badge label="Reorder" color={C.red} bg={C.redBg} border={C.redBorder} /> : <Badge label="OK" color={C.accent} bg={C.accentLight} border="#A8D8B8" />}</Td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
      </Card>
      {showModal && (
        <Modal title="Add Supply" onClose={()=>setShowModal(false)}>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
            <Inp label="Item Name" value={form.name} onChange={f('name')} placeholder="e.g. Neem Oil" />
            <Inp label="Category" value={form.category} onChange={f('category')} opts={['pesticide','fungicide','fertilizer','herbicide','soil amendment','tools','seeds','other']} />
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
            <Inp label="Current Stock" value={form.quantity} onChange={f('quantity')} type="number" />
            <Inp label="Unit" value={form.unit} onChange={f('unit')} opts={['litres','ml','kg','grams','bags','pieces','bottles']} />
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
            <Inp label="Reorder Level" value={form.reorder_level} onChange={f('reorder_level')} type="number" />
            <Inp label="Cost per Unit (Rs)" value={form.cost_per_unit} onChange={f('cost_per_unit')} type="number" />
          </div>
          <Inp label="Supplier" value={form.supplier} onChange={f('supplier')} placeholder="Supplier name" />
          <div style={{ display:'flex', gap:10 }}>
            <Btn onClick={save} style={{ flex:1, justifyContent:'center' }}>Save</Btn>
            <Btn variant="ghost" onClick={()=>setShowModal(false)} style={{ flex:1, justifyContent:'center' }}>Cancel</Btn>
          </div>
        </Modal>
      )}
    </div>
  );
}

function PestsTab({ zones }) {
  const [pests, setPests] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ zone_id:'', pest_name:'', severity:'low', treatment_used:'', notes:'' });
  const f = k => v => setForm(p=>({...p,[k]:v}));

  const load = () => { fetch(`${API}/pests`).then(r=>r.json()).then(d=>setPests(Array.isArray(d)?d:[])).catch(()=>{}); };
  useEffect(() => { load(); }, []);

  const save = async () => {
    await fetch(`${API}/pests`, { method:'POST', headers:{'Content-Type':'application/json'}, body:JSON.stringify(form) });
    setShowModal(false); load();
  };

  const resolve = async (id) => {
    await fetch(`${API}/pests/${id}/resolve`, { method:'PATCH' });
    load();
  };

  const severityColor = s => s==='high'?[C.red,C.redBg,C.redBorder]:s==='medium'?[C.amber,C.amberBg,C.amberBorder]:[C.accent,C.accentLight,'#A8D8B8'];

  return (
    <div style={{ padding:'24px 28px' }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
        <div style={{ fontSize:20, fontWeight:800, color:C.text }}>Pest and Disease Alerts</div>
        <Btn onClick={()=>setShowModal(true)}>+ Report Alert</Btn>
      </div>
      {pests.length===0
        ? <Card style={{ padding:40, textAlign:'center', color:C.accent, fontWeight:600 }}>No active pest alerts - all clear!</Card>
        : pests.map(p => {
          const [col,bg,brd] = severityColor(p.severity);
          return (
            <Card key={p.id} style={{ padding:20, marginBottom:12, borderLeft:`4px solid ${col}` }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
                <div style={{ flex:1 }}>
                  <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:6 }}>
                    <span style={{ fontSize:15, fontWeight:800, color:C.text }}>{p.pest_name}</span>
                    <Badge label={`${p.severity} severity`} color={col} bg={bg} border={brd} />
                  </div>
                  <div style={{ fontSize:13, color:C.text3 }}>Zone: {p.zone_name} - Detected: {p.detected_date?.slice(0,10)}</div>
                  {p.treatment_used && <div style={{ fontSize:13, color:C.text2, marginTop:4 }}>Treatment: {p.treatment_used}</div>}
                </div>
                <Btn variant="light" onClick={()=>resolve(p.id)} style={{ padding:'5px 14px', fontSize:12 }}>Resolve</Btn>
              </div>
            </Card>
          );
        })}
      {showModal && (
        <Modal title="Report Pest Alert" onClose={()=>setShowModal(false)}>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
            <Inp label="Zone" value={form.zone_id} onChange={f('zone_id')} opts={[{value:'',label:'Select zone'},...zones.map(z=>({value:z.id,label:z.name}))]} />
            <Inp label="Severity" value={form.severity} onChange={f('severity')} opts={['low','medium','high']} />
          </div>
          <Inp label="Pest or Disease Name" value={form.pest_name} onChange={f('pest_name')} placeholder="e.g. Aphids, Powdery Mildew" />
          <Inp label="Treatment Applied" value={form.treatment_used} onChange={f('treatment_used')} placeholder="e.g. Neem oil spray 2%" />
          <Inp label="Notes" value={form.notes} onChange={f('notes')} placeholder="Affected area, spread..." />
          <div style={{ display:'flex', gap:10 }}>
            <Btn onClick={save} style={{ flex:1, justifyContent:'center' }}>Report</Btn>
            <Btn variant="ghost" onClick={()=>setShowModal(false)} style={{ flex:1, justifyContent:'center' }}>Cancel</Btn>
          </div>
        </Modal>
      )}
    </div>
  );
}

function NurseryTab() {
  const [stock, setStock] = useState([]);
  const [showStock, setShowStock] = useState(false);
  const [showSale, setShowSale] = useState(null);
  const [stockForm, setStockForm] = useState({ name:'', category:'flowering', quantity:'', selling_price:'', cost_price:'', pot_size:'' });
  const [saleForm, setSaleForm] = useState({ quantity:'1', unit_price:'', customer_name:'', customer_phone:'', payment_mode:'cash' });
  const sf = k => v => setStockForm(p=>({...p,[k]:v}));
  const rf = k => v => setSaleForm(p=>({...p,[k]:v}));

  const load = () => { fetch(`${API}/nursery/stock`).then(r=>r.json()).then(d=>setStock(Array.isArray(d)?d:[])).catch(()=>{}); };
  useEffect(() => { load(); }, []);

  const saveStock = async () => {
    await fetch(`${API}/nursery/stock`, { method:'POST', headers:{'Content-Type':'application/json'}, body:JSON.stringify(stockForm) });
    setShowStock(false); load();
  };

  const makeSale = async () => {
    await fetch(`${API}/nursery/sale`, { method:'POST', headers:{'Content-Type':'application/json'},
      body:JSON.stringify({ ...saleForm, stock_id:showSale.id, unit_price:saleForm.unit_price||showSale.selling_price }) });
    setShowSale(null); load();
  };

  return (
    <div style={{ padding:'24px 28px' }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
        <div style={{ fontSize:20, fontWeight:800, color:C.text }}>Nursery and Retail</div>
        <Btn onClick={()=>setShowStock(true)}>+ Add Stock</Btn>
      </div>
      {stock.length===0 ? <Card style={{ padding:40, textAlign:'center', color:C.text3 }}>No nursery stock added yet</Card> : (
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(200px,1fr))', gap:14 }}>
          {stock.map(s => (
            <Card key={s.id} style={{ padding:18 }}>
              <div style={{ fontSize:14, fontWeight:800, color:C.text, marginBottom:4 }}>{s.name}</div>
              <div style={{ fontSize:11, color:C.text3, marginBottom:10 }}>{s.category}{s.pot_size?` - ${s.pot_size}`:''}</div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8, marginBottom:12 }}>
                <div style={{ background:C.bg, borderRadius:7, padding:'8px 10px' }}>
                  <div style={{ fontSize:10, color:C.text3 }}>Stock</div>
                  <div style={{ fontSize:15, fontWeight:700, color:s.quantity<5?C.red:C.accent }}>{s.quantity}</div>
                </div>
                <div style={{ background:C.bg, borderRadius:7, padding:'8px 10px' }}>
                  <div style={{ fontSize:10, color:C.text3 }}>Price</div>
                  <div style={{ fontSize:15, fontWeight:700 }}>{inr(s.selling_price)}</div>
                </div>
              </div>
              <Btn onClick={()=>{ setSaleForm({quantity:'1',unit_price:s.selling_price,customer_name:'',customer_phone:'',payment_mode:'cash'}); setShowSale(s); }}
                style={{ width:'100%', justifyContent:'center', fontSize:13 }} disabled={s.quantity<1}>
                Sell
              </Btn>
            </Card>
          ))}
        </div>
      )}
      {showStock && (
        <Modal title="Add Nursery Stock" onClose={()=>setShowStock(false)}>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
            <Inp label="Plant Name" value={stockForm.name} onChange={sf('name')} placeholder="e.g. Rose - Red" />
            <Inp label="Category" value={stockForm.category} onChange={sf('category')} opts={['flowering','foliage','fruit','herb','cactus','bonsai','creeper','grass','other']} />
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
            <Inp label="Quantity" value={stockForm.quantity} onChange={sf('quantity')} type="number" />
            <Inp label="Pot Size" value={stockForm.pot_size} onChange={sf('pot_size')} placeholder="e.g. 6 inch" />
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
            <Inp label="Selling Price (Rs)" value={stockForm.selling_price} onChange={sf('selling_price')} type="number" />
            <Inp label="Cost Price (Rs)" value={stockForm.cost_price} onChange={sf('cost_price')} type="number" />
          </div>
          <div style={{ display:'flex', gap:10 }}>
            <Btn onClick={saveStock} style={{ flex:1, justifyContent:'center' }}>Add to Stock</Btn>
            <Btn variant="ghost" onClick={()=>setShowStock(false)} style={{ flex:1, justifyContent:'center' }}>Cancel</Btn>
          </div>
        </Modal>
      )}
      {showSale && (
        <Modal title={`Sell - ${showSale.name}`} onClose={()=>setShowSale(null)}>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
            <Inp label="Quantity" value={saleForm.quantity} onChange={rf('quantity')} type="number" />
            <Inp label="Unit Price (Rs)" value={saleForm.unit_price} onChange={rf('unit_price')} type="number" />
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
            <Inp label="Customer Name" value={saleForm.customer_name} onChange={rf('customer_name')} placeholder="Optional" />
            <Inp label="Customer Phone" value={saleForm.customer_phone} onChange={rf('customer_phone')} placeholder="Optional" />
          </div>
          <Inp label="Payment Mode" value={saleForm.payment_mode} onChange={rf('payment_mode')} opts={['cash','card','upi','credit']} />
          <div style={{ padding:'12px 16px', background:C.accentLight, borderRadius:9, marginBottom:14, display:'flex', justifyContent:'space-between' }}>
            <span style={{ fontSize:13, color:C.text2 }}>Total Amount</span>
            <span style={{ fontSize:16, fontWeight:800, color:C.accent }}>{inr((saleForm.quantity||0)*(saleForm.unit_price||0))}</span>
          </div>
          <div style={{ display:'flex', gap:10 }}>
            <Btn onClick={makeSale} style={{ flex:1, justifyContent:'center' }}>Confirm Sale</Btn>
            <Btn variant="ghost" onClick={()=>setShowSale(null)} style={{ flex:1, justifyContent:'center' }}>Cancel</Btn>
          </div>
        </Modal>
      )}
    </div>
  );
}

function AnalyticsTab() {
  const [data, setData] = useState(null);
  useEffect(() => { fetch(`${API}/analytics`).then(r=>r.json()).then(setData).catch(()=>{}); }, []);

  if (!data) return <div style={{ padding:40, textAlign:'center', color:C.text3 }}>Loading analytics...</div>;

  const totalRev = (data.revenue_by_zone||[]).reduce((s,z)=>s+parseFloat(z.revenue||0),0);

  return (
    <div style={{ padding:'24px 28px' }}>
      <div style={{ fontSize:20, fontWeight:800, color:C.text, marginBottom:20 }}>Analytics and AI Insights</div>
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16, marginBottom:16 }}>
        <Card style={{ padding:20 }}>
          <div style={{ fontSize:15, fontWeight:800, color:C.text, marginBottom:14 }}>Top Producing Crops</div>
          {(data.top_crops||[]).length===0 ? <Empty msg="No harvest data yet" />
            : (data.top_crops||[]).map((c,i) => (
              <div key={i} style={{ display:'flex', justifyContent:'space-between', marginBottom:10 }}>
                <div>
                  <div style={{ fontSize:13, fontWeight:600 }}>{c.name}</div>
                  <div style={{ fontSize:11, color:C.text3 }}>{parseFloat(c.total_qty||0).toFixed(1)} kg harvested</div>
                </div>
                <div style={{ fontSize:13, fontWeight:700, color:C.accent }}>{inr(c.total_rev||0)}</div>
              </div>
            ))}
        </Card>
        <Card style={{ padding:20 }}>
          <div style={{ fontSize:15, fontWeight:800, color:C.text, marginBottom:14 }}>Revenue by Zone</div>
          {(data.revenue_by_zone||[]).length===0 ? <Empty msg="No revenue data yet" />
            : (data.revenue_by_zone||[]).map((z,i) => {
              const pct = totalRev>0?(parseFloat(z.revenue||0)/totalRev)*100:0;
              return (
                <div key={i} style={{ marginBottom:12 }}>
                  <div style={{ display:'flex', justifyContent:'space-between', marginBottom:4 }}>
                    <span style={{ fontSize:13 }}>{z.name}</span>
                    <span style={{ fontSize:13, fontWeight:700, color:C.accent }}>{inr(z.revenue)}</span>
                  </div>
                  <div style={{ height:5, background:C.border, borderRadius:99 }}>
                    <div style={{ height:5, width:`${pct}%`, background:C.accent, borderRadius:99 }} />
                  </div>
                </div>
              );
            })}
        </Card>
      </div>
      <Card style={{ padding:20, background:'linear-gradient(135deg,#0A1A0F,#0F2A14)', border:'none' }}>
        <div style={{ fontSize:14, fontWeight:700, color:C.accent, marginBottom:14 }}>AI Recommendations</div>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(220px,1fr))', gap:12 }}>
          {[
            ['Spray Optimization', 'Schedule critical sprays Tuesday mornings for highest efficiency based on task history.'],
            ['Yield Improvement', 'Vegetable garden showing consistent output. Consider expanding by 20% next season.'],
            ['Compost Savings', 'Full vermicompost utilization can reduce fertilizer costs by 40-50%.'],
            ['Milk Optimization', 'Morning sessions yield 30% more. Feed cows 2 hours before morning milking.'],
            ['Nursery Pricing', 'Seasonal demand peaks October-November. Stock up flowering plants by September.'],
            ['Mycorrhizae', 'Inoculating vegetable seedlings can improve yield 15-25%. Start pilot batch now.'],
          ].map(([title, text]) => (
            <div key={title} style={{ padding:'14px 16px', background:'rgba(58,175,88,.08)', borderRadius:10, border:'1px solid rgba(58,175,88,.2)' }}>
              <div style={{ fontSize:12, fontWeight:700, color:C.accent, marginBottom:6 }}>{title}</div>
              <div style={{ fontSize:12, color:'rgba(240,248,242,.7)', lineHeight:1.5 }}>{text}</div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}

export default function Horticulture() {
  const [tab, setTab] = useState('dashboard');
  const [zones, setZones] = useState([]);

  const loadZones = useCallback(() => {
    fetch(`${API}/zones`).then(r=>r.json()).then(d=>setZones(Array.isArray(d)?d:[])).catch(()=>{});
  }, []);

  useEffect(() => { loadZones(); }, [loadZones]);

  const tabs = [
    ['dashboard','Dashboard'],['zones','Zones'],['plants','Plants'],
    ['tasks','Tasks'],['harvests','Harvests'],['dairy','Dairy'],
    ['compost','Compost'],['supplies','Supplies'],['pests','Pests'],
    ['nursery','Nursery'],['analytics','Analytics'],
  ];

  return (
    <div style={{ background:C.bg, minHeight:'100vh' }}>
      <div style={{ background:C.surface, borderBottom:`1px solid ${C.border}`, padding:'0 28px', position:'sticky', top:0, zIndex:50 }}>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', height:60 }}>
          <div style={{ fontSize:20, fontWeight:800, color:C.text }}>Horticulture Management</div>
          <div style={{ fontSize:12, color:C.text3 }}>{zones.length} active zones</div>
        </div>
        <div style={{ display:'flex', gap:2, overflowX:'auto' }}>
          {tabs.map(([id, label]) => (
            <button key={id} onClick={()=>setTab(id)}
              style={{ padding:'10px 16px', border:'none', background:'transparent',
                color: tab===id?C.accent:C.text3, fontSize:13, fontWeight:tab===id?700:500,
                cursor:'pointer', whiteSpace:'nowrap',
                borderBottom: tab===id?`2px solid ${C.accent}`:'2px solid transparent' }}>
              {label}
            </button>
          ))}
        </div>
      </div>
      {tab==='dashboard' && <HortDashboard zones={zones} setTab={setTab} />}
      {tab==='zones' && <ZonesTab zones={zones} reload={loadZones} />}
      {tab==='plants' && <PlantsTab zones={zones} />}
      {tab==='tasks' && <TasksTab zones={zones} />}
      {tab==='harvests' && <HarvestsTab zones={zones} />}
      {tab==='dairy' && <DairyTab />}
      {tab==='compost' && <CompostTab />}
      {tab==='supplies' && <SuppliesTab />}
      {tab==='pests' && <PestsTab zones={zones} />}
      {tab==='nursery' && <NurseryTab />}
      {tab==='analytics' && <AnalyticsTab />}
    </div>
  );
}
